/**
 * The API client.
 *
 * ⚠️ RELATIVE PATHS, NO BASE URL, AND THAT IS THE WHOLE POINT. The app and its API are one origin
 * (CLAUDE.md §4): in production one process serves both, and in development Vite proxies. A base URL
 * here would be the first step toward a cross-origin arrangement that needs CORS and a
 * `SameSite=None` cookie — on the credential the whole arrangement exists to protect.
 *
 * ⚠️ AND FAILURES CARRY THE SERVER'S OWN SENTENCE. There is no client-side table of error codes to
 * go stale: the server sends `{ code, message: { en, ar } }` and the screen renders the language the
 * customer is reading. What the client switches on is the CODE — `handle.taken` belongs beside the
 * field somebody is typing in, not in a banner with a Retry button that can never help.
 */

import type { Issue, Site, SubscriptionStatus } from '@ksa/model'
import { track } from './analytics.ts'
import { safeToReload } from './useDeployWatch.ts'

export type SiteSummary = {
  id: string
  /** Absent until the customer claims one — building reserves no address. */
  handle: string | null
  name: string
  createdAt: string
  updatedAt: string
  /**
   * ⚠️ ITS PRESENCE IS NOT "live" ANY MORE — `takenDownAt` is. Publishing is free for a week and the
   * sweep takes an unpaid site off the internet when the week is up, which leaves a record that HAS
   * published and is not reachable. Every screen reading this uses `isLive` below rather than a
   * truthiness check, because the old test is wrong in precisely the case a customer needs told
   * about.
   */
  published: { version: number; at: string; origin: string; from?: string; expiresAt?: string; takenDownAt?: string } | null
  subscription: { status: SubscriptionStatus; renewsAt?: string; providerRef?: string } | null
}

/** Whether a visitor would reach this site right now. */
export function isLive(site: Pick<SiteSummary, 'published'>): boolean {
  return Boolean(site.published && !site.published.takenDownAt)
}

/**
 * Whether what a visitor gets is the document on our screens.
 *
 * ⚠️ ONE FUNCTION, TWO CONSUMERS — the editor's header, which offers «Open» or «Publish» from it, and
 * the site card, which offers «Publish changes». It was written twice and each copy compared
 * `updatedAt` with `published.at`: two clock readings taken by different components a moment apart,
 * which is an ordering that is true or false depending on how fast the machine is. Publishing then
 * reloading put the header back to «Publish» on a site nobody had touched.
 *
 * ⚠️ IT IS AN EQUALITY, AND THAT IS THE FIX RATHER THAN A TIGHTER COMPARISON. `published.from` is the
 * record's own `updatedAt` at the moment the snapshot was rendered, so this asks "was it made from
 * exactly this?" — a question with no clock in it.
 *
 * > ⚠️ **THE DATE COMPARISON SURVIVES AS A FALLBACK, FOR SNAPSHOTS WRITTEN BEFORE `from` EXISTED.**
 * > It is the old, racy rule and it is right often enough for a record that will be replaced by its
 * > owner's next publish. It can go once no live site predates that change.
 */
export function snapshotIsCurrent(site: Pick<SiteSummary, 'published' | 'updatedAt'>): boolean {
  const published = site.published
  if (!published || published.takenDownAt) return false
  if (published.from !== undefined) return published.from === site.updatedAt
  return new Date(site.updatedAt) <= new Date(published.at)
}

/**
 * Whole days left of the free week, or `null` when there is no deadline — a paid site, or one never
 * published.
 *
 * ⚠️ IT ROUNDS UP AND FLOORS AT ZERO, because this number is read as a promise. Six and a half days
 * rendered as "6 days left" is a day the customer thinks they do not have; and a negative count on a
 * site the sweep has not reached yet is a number nobody can act on.
 */
export function trialDaysLeft(site: Pick<SiteSummary, 'published'>): number | null {
  const expiresAt = site.published?.expiresAt
  if (!expiresAt || site.published?.takenDownAt) return null
  const ms = new Date(expiresAt).getTime() - Date.now()
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)))
}

export type SiteRecord = SiteSummary & { document: Site }

export type SessionInfo = {
  signedIn: boolean
  person: { name: string; email: string } | null
  price: { amountMinor: number; currency: string; vatInclusive: boolean; net: number; vat: number; gross: number }
  billing: { real: boolean; provider: string }
}

export class ApiFailure extends Error {
  constructor(
    readonly code: string,
    readonly text: { en: string; ar: string },
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(text.en)
  }
}

/**
 * Somebody who is not signed in is sent to sign in, rather than shown a page that explains it.
 *
 * ⚠️ HERE RATHER THAN ON EACH SCREEN, because "you are not signed in" is one rule and it had two
 * half-implementations: the sites list rendered an error notice with a link in it, and the editor
 * rendered a different error notice with no link at all — so arriving at a bookmarked site with an
 * expired session was a dead end. One declaration, every consumer (CLAUDE.md §0.5).
 *
 * ⚠️ IT KEYS ON `auth.required`, NOT ON 401. Codes are the contract; a status is shared by every
 * other reason a request can be refused, and a future one would silently start throwing people out
 * of the application.
 *
 * ⚠️ AND `/session` CANNOT TRIP IT: that endpoint answers 200 with `signedIn: false`, deliberately,
 * because the shell asks it on every page including `/` and `/report` — and `/report` is for a
 * stranger who landed on a customer's site and must never be asked to sign in to complain about it.
 */
let leaving = false

/**
 * ⚠️ THE BOUNCE GUARD, AND IT IS NOT THEORETICAL — A BROWSER DRIVE PRODUCED THE LOOP ON THE FIRST
 * RUN OF THIS FEATURE. Redirecting on `auth.required` assumes that signing in FIXES `auth.required`,
 * and there are real reasons it does not: a rotated `SESSION_SECRET` invalidates a cookie the server
 * then keeps refusing, a clock far enough out makes every token expired on arrival, a browser
 * configured to drop the cookie stores nothing to send back. In each case the app lands on the page,
 * is refused, goes to sign in, succeeds, comes back, and is refused again — for ever.
 *
 * ⚠️ AND THE COST OF THAT LOOP IS NOT A SPINNING TAB. It is a full page load plus an OIDC round trip
 * per iteration against a service pinned to `--max-instances 1` with a concurrency of 40 (§5.9), from
 * a customer who cannot stop it because the tab never settles. A handful of people in this state is
 * the builder's entire capacity.
 *
 * One attempt per cooldown. If the second refusal arrives inside it, signing in demonstrably did not
 * help, and the screen's own error — which is why the sites list still renders one — is the honest
 * answer rather than another lap.
 */
const BOUNCE_KEY = 'ksa:auth-bounce'
const BOUNCE_COOLDOWN_MS = 15_000

function bouncedRecently(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(BOUNCE_KEY) ?? 0)
    if (Date.now() - last < BOUNCE_COOLDOWN_MS) return true
    window.sessionStorage.setItem(BOUNCE_KEY, String(Date.now()))
    return false
  } catch {
    // ⚠️ STORAGE CAN THROW, AND THE REDIRECT STILL HAPPENS. Some privacy modes refuse
    // `sessionStorage` outright; refusing to sign anybody in there would be breaking the common case
    // to protect against the rare one. A loop needs storage to be unavailable AND sign-in to be
    // persistently broken — and `leaving` still bounds it within a single page.
    return false
  }
}

function toSignIn(): void {
  // ⚠️ ONCE. A screen makes several requests at a time and they fail together; without this, each
  // one assigns `location.href` and the browser is handed the same navigation five times.
  if (leaving) return

  /**
   * ⚠️ NOT WHILE THERE IS UNSAVED WORK, AND THIS IS THE ONE CASE WORTH THE EXTRA LINE. A session that
   * expires while somebody is typing would otherwise navigate away mid-sentence and lose it — the
   * save already failed, and throwing the page away is a second, larger loss on top of the first.
   * They keep their work and the drawer says it is not saved; the header still offers Sign in.
   *
   * `safeToReload` is the deploy watcher's own question, reused rather than re-asked: "would
   * anything on screen lose work if this page went away". There is one answer to that and it lives
   * in one place.
   */
  if (!safeToReload()) return
  if (bouncedRecently()) return

  leaving = true
  const returnTo = `${window.location.pathname}${window.location.search}`
  window.location.href = `/auth/start?returnTo=${encodeURIComponent(returnTo)}`
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  let response: Response
  try {
    response = await fetch(path, {
      method,
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      // Same origin, so the cookie rides along without `credentials: 'include'` — which is the
      // setting that would be needed cross-origin and is deliberately absent.
      ...(signal ? { signal } : {}),
    })
  } catch (error) {
    // ⚠️ A NETWORK FAILURE IS NOT A SERVER FAILURE, and telling them apart is what stops a customer
    // on a train being shown "something went wrong on our side".
    if ((error as Error).name === 'AbortError') throw error
    throw new ApiFailure(
      'network',
      {
        en: 'Could not reach the server. Check your connection.',
        ar: 'تعذّر الوصول إلى الخادم. تحقّق من اتصالك.',
      },
      0,
    )
  }

  if (response.status === 204) return undefined as T

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const failure = payload as { code?: string; message?: { en: string; ar: string }; details?: unknown } | null
    if (failure?.code === 'auth.required') toSignIn()
    throw new ApiFailure(
      failure?.code ?? 'unknown',
      failure?.message ?? { en: 'Something went wrong.', ar: 'حدث خطأ ما.' },
      response.status,
      failure?.details,
    )
  }

  return payload as T
}

export const api = {
  session: (signal?: AbortSignal) => request<SessionInfo>('GET', '/api/session', undefined, signal),

  sites: (signal?: AbortSignal) => request<SiteSummary[]>('GET', '/api/sites', undefined, signal),
  site: (id: string, signal?: AbortSignal) => request<SiteRecord>('GET', `/api/sites/${encodeURIComponent(id)}`, undefined, signal),

  /**
   * ⚠️ `document` IS FOR ONE CALLER: THE DEMONSTRATION BEING KEPT. Everything else sends a WORD
   * ("restaurant") and lets the server build the starter from `@ksa/model`, which is why a new site
   * is always one the editor and the validator recognise. The exception exists because a visitor has
   * spent five minutes editing an example that was never saved anywhere, and the only way to keep
   * what is on their screen is to send it. The server bounds the size and publish validates the
   * meaning — see CLAUDE.md §2.9.
   */
  createSite: (input: { name: string; starter?: string; document?: Site }) =>
    request<SiteRecord>('POST', '/api/sites', input),

  saveDocument: (id: string, document: Site) =>
    request<SiteSummary>('PUT', `/api/sites/${encodeURIComponent(id)}/document`, document),

  /**
   * ⚠️ DELETING RELEASES THE ADDRESS, IN THE SAME OPERATION. The screen says so before the click —
   * forgetting it is the quiet failure where a site disappears while its address stays claimed for
   * ever, so the customer cannot recreate what they just deleted and nothing says why.
   */
  deleteSite: (id: string) => request<void>('DELETE', `/api/sites/${encodeURIComponent(id)}`),

  /**
   * Upload a picture and get back its key.
   *
   * ⚠️ RAW BYTES WITH A CONTENT TYPE, NOT `FormData`. The browser has already re-encoded it to one
   * WebP with no metadata (see `upload.ts`), so there is nothing multipart would carry — and not
   * using it means no parser dependency on the server. See `routes/media.ts`.
   */
  uploadMedia: async (id: string, blob: Blob): Promise<{ key: string; bytes: number }> => {
    const response = await fetch(`/api/sites/${encodeURIComponent(id)}/media`, {
      method: 'POST',
      headers: { 'content-type': blob.type || 'application/octet-stream' },
      body: blob,
    })
    const body = (await response.json().catch(() => ({}))) as {
      code?: string
      message?: { en: string; ar: string }
      key?: string
      bytes?: number
    }
    if (!response.ok) {
      throw new ApiFailure(
        body.code ?? 'request.failed',
        body.message ?? { en: 'That upload did not work.', ar: 'لم ينجح الرفع.' },
        response.status,
      )
    }
    return { key: body.key ?? '', bytes: body.bytes ?? 0 }
  },

  /** Claim an address, or move to a different one. The only place an address becomes real. */
  claimHandle: (id: string, handle: string) =>
    request<SiteSummary>('PUT', `/api/sites/${encodeURIComponent(id)}/handle`, { handle }),

  handleAvailable: (handle: string, signal?: AbortSignal) =>
    request<{ handle: string; available: boolean; reason: string | null }>(
      'GET',
      `/api/handles/${encodeURIComponent(handle)}`,
      undefined,
      signal,
    ),

  checks: (id: string, signal?: AbortSignal) =>
    request<{
      errors: Issue[]
      warnings: Issue[]
      canPublish: boolean
      subscription: SiteSummary['subscription']
      published: SiteSummary['published']
      /** How long a site published now stays up unpaid. From the server, so the number on screen and
       *  the number the sweep enforces cannot drift apart. */
      trialDays: number
      /**
       * Which version of the publishing statement this site's owner has already agreed to, if any.
       *
       * ⚠️ THE WIZARD READS IT SO IT CAN SHOW AGREEING AS A FACT RATHER THAN ASK AGAIN. Consent is
       * given once per site (see `PUBLISH_CONSENT`), and a checkbox that returns on every republish
       * is one nobody reads by the third time.
       */
      consent: { at: string; version: string } | null
    }>('GET', `/api/sites/${encodeURIComponent(id)}/checks`, undefined, signal),

  /**
   * ⚠️ THE CONSENT TRAVELS IN THE REQUEST, NOT IN A DISABLED BUTTON. The server refuses without it —
   * a button that cannot be pressed proves only that a browser agreed — so this argument is the
   * whole mechanism rather than a mirror of one.
   */
  publish: (id: string, consent?: boolean) =>
    request<{
      version: number
      files: number
      url: string
      warnings: Issue[]
      liveWithinSeconds: number
      published: NonNullable<SiteSummary['published']>
    }>('POST', `/api/sites/${encodeURIComponent(id)}/publish`, consent ? { consent: true } : {}),

  unpublish: (id: string) => request<{ ok: true }>('POST', `/api/sites/${encodeURIComponent(id)}/unpublish`),

  subscribe: (id: string, returnTo: string) =>
    request<{ payUrl?: string; alreadyActive?: boolean; subscription: SiteSummary['subscription']; real?: boolean }>(
      'POST',
      `/api/sites/${encodeURIComponent(id)}/subscribe`,
      { returnTo },
    ),

  confirmSubscription: (id: string, ref: string) =>
    request<{ subscription: SiteSummary['subscription'] }>('POST', `/api/sites/${encodeURIComponent(id)}/subscription/confirm`, { ref }),

  cancelSubscription: (id: string) =>
    request<{ subscription: SiteSummary['subscription'] }>('POST', `/api/sites/${encodeURIComponent(id)}/subscription/cancel`),

  /** `anonymous` is whether a stranger may build RIGHT NOW — the day's budget and the concurrency
   *  cap, answered before the button is pressed rather than after half a minute of waiting. */
  aiAvailable: (signal?: AbortSignal) =>
    request<{ available: boolean; anonymous: boolean }>('GET', '/api/ai/available', undefined, signal),

  /**
   * ⚠️ THE ANALYTICS EVENTS ARE HERE, NOT AT THE THREE BUTTONS, and that is the only reason they are
   * in the API client at all. Generation is reached from the landing page, from the same landing
   * page AFTER a sign-in round trip that resumes it on `/sites`, and from the new-site form — three
   * call sites and one act. Counted at each button, the day somebody adds a fourth entry point the
   * funnel quietly under-reports and nothing says so. This is the choke point they all pass through.
   */
  /**
   * ⚠️ `id` IS `null` FOR A CALLER WITH NO SESSION, AND THAT IS THE CONTRACT RATHER THAN AN OMISSION.
   * Building is free and needs no account, so a stranger gets the DOCUMENT back and nothing is stored
   * — the browser keeps it in its own tab exactly as it keeps one of the landing page's examples, and
   * a site is created the moment they say to keep it. Branch on the id, never on the status code.
   */
  generate: async (description: string, lang: 'en' | 'ar') => {
    track('generate_start')
    try {
      const created = await request<{ id: string | null; handle: string | null; name: string; document: Site; plan: unknown }>(
        'POST',
        '/api/ai/site',
        { description, lang },
      )
      track('generate_done')
      return created
    } catch (error) {
      track('generate_failed')
      throw error
    }
  },

  /**
   * Translate one field of copy.
   *
   * ⚠️ IT RESOLVES TO A STRING AND WRITES NOTHING. The caller decides what to do with the result,
   * which is what keeps a translation from being able to touch anything the customer was not
   * editing — see `ai/translate.ts` on why the model is never handed a document.
   */
  translate: (args: { source: string; from: 'en' | 'ar'; to: 'en' | 'ar'; label?: string; siteName?: string }) =>
    request<{ text: string }>('POST', '/api/ai/translate', args).then((reply) => reply.text),

  /**
   * A site's worth of blanks, in one request.
   *
   * ⚠️ IT SENDS STRINGS AND IDS OF THIS BROWSER'S OWN MAKING — never a site id, never a document.
   * `LanguageFill` numbers the gaps, the reply comes back under the same numbers, and `applyGap`
   * writes each one into the document here. So a route that fills in a language cannot read a site,
   * cannot be asked to read somebody else's, and needs no session to authorise.
   */
  translateMany: (args: {
    items: { id: string; label: string; source: string }[]
    to: 'en' | 'ar'
    siteName?: string
  }) =>
    request<{ texts: Record<string, string> }>('POST', '/api/ai/translate-many', args).then(
      (reply) => reply.texts,
    ),
}

/** Where a published site lives. Derived from the handle rather than stored, so a domain change is
 *  one constant rather than a migration of every record. */
export function publishedUrl(handle: string): string {
  return `https://${handle}.ksa-web.app`
}
